/**
 * The operator's OWN passkeys — the self-service surface behind the header user menu.
 *
 * ⛔ **EVERY PATH BELOW WAS READ OFF `cp-auth-api`'s CONTROLLER, NOT COPIED FROM THE DP SHELL.**
 * The owner asked for *"the whole header from DP"*, and the DP shell's equivalent module
 * (`cloud/dp/shell/src/home/api/home-api.ts`) talks to DP `core-api`. This console authenticates
 * against **`cp-auth-api`**, so the two consoles share the *shape* of this feature and none of its
 * endpoints. A DP path pasted here is a 404 at best and a wrong-principal call at worst.
 *
 * Measured against `cloud/cp/cp-auth-api/src/modules/passkey/passkey.controller.ts` on
 * 2026-08-05 — `@Controller('v1/auth')`, so every route below is `/v1/auth/…`:
 *
 * | verb | route | `file:line` | guards |
 * |---|---|---|---|
 * | list   | `GET /v1/auth/passkeys`                 | `:89-90`   | `CpJwtAuthGuard` + `NoDelegatedPrincipalGuard` |
 * | options| `POST /v1/auth/passkey/register/options` | `:116-117` | `CpJwtAuthGuard` + `NoDelegatedPrincipalGuard` |
 * | create | `POST /v1/auth/passkeys`                | `:126-127` | `CpJwtAuthGuard` + `NoDelegatedPrincipalGuard` |
 * | delete | `DELETE /v1/auth/passkeys/:id`          | `:174-176` | `CpJwtAuthGuard` + `NoDelegatedPrincipalGuard` |
 *
 * ⚠️ **NO `@RequirePermission` ON ANY OF THEM, AND THAT IS CORRECT RATHER THAN AN OVERSIGHT.**
 * All four derive the operator id from the authenticated token (`req.user.id`) and never from a
 * path param, so the caller can only ever reach their own credentials — `passkey.service.ts:322`
 * scopes the delete with `and(eq(id, passkeyId), eq(operatorId, operatorId))`, so a guessed id
 * belonging to somebody else 404s rather than deleting. There is a separate, admin-facing
 * `GET /v1/operators/:id/passkeys` gated by `cp:operator:read` (`:206-207`) and a separate proxied
 * read in `identity.api.ts` (E76) — ⛔ neither is this, and they must not be confused: those answer
 * *"what does that operator hold"*, these answer *"what do I hold"*.
 *
 * ⚠️ `/v1/auth` is a `PathPrefix` match straight to cp-auth-api
 * (`charts/zion-cp/templates/ingress/httproute.yaml:75`), which is the same door
 * `platform/auth/auth.api.ts` already goes through for login — so no gateway change is needed and
 * none was made.
 *
 * ⛔ **`NoDelegatedPrincipalGuard` is on all four, and this console is unaffected by design.** A
 * first-party browser session carries no `scp` claim, so the guard is a byte-identical passthrough
 * for us; it exists to stop an OAuth/MCP delegated credential enrolling an authenticator against
 * its minter's `sub` and then logging in as a full operator through the `@Public()`
 * `POST passkey/login`. ⇒ Nothing here needs to work around it, and nothing here should try to.
 */
import { startRegistration } from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import {
  RECOVERY_CODES_ROUTE,
  recoveryCodesIssuedSchema,
  recoveryCodesStatusSchema,
  type RecoveryCodesIssued,
  type RecoveryCodesStatus,
} from '@zion/sdk-contracts';
import { api } from '../platform/http/client';

/**
 * WebAuthn registration options exactly as cp-auth-api returns them.
 *
 * ⛔ ALIASES THE LIBRARY'S OWN JSON TYPE, for the reason `auth.api.ts` states at length for the
 * authentication counterpart: `passkey.service.ts:78-91` returns `generateRegistrationOptions()`'s
 * output verbatim and the only consumer hands it straight to `startRegistration({ optionsJSON })`.
 * A hand-rolled copy widens `type` from the literal `'public-key'` to `string`, which is what forced
 * an `as unknown as` double-cast at the DP call site (`PasskeySettingsPage.tsx:42-44`) — banned
 * repo-wide, and it silences every future divergence at this seam rather than just that field.
 */
export type PasskeyRegistrationOptions = PublicKeyCredentialCreationOptionsJSON;

export type { RegistrationResponseJSON };

/**
 * One of the caller's own passkeys, as `OperatorPasskeySummary`
 * (`passkey.controller.ts:38-43`) projects it.
 *
 * ⭐ **`lastUsedAt` IS NOW A REAL COLUMN READ — WS-4 G6 / C10 made the producer change.** This
 * docblock previously explained why the field was deliberately absent from this interface: the
 * producer sent it **hardcoded `null`** (`passkey.controller.ts:106`, a literal, not a column read),
 * so declaring it would have read to the next author as a field that is *sometimes* known when in
 * fact *nothing could ever populate it*. That producer change has landed —
 * `cp_auth.operator_passkeys.last_used_at` exists (migration `0029`), `passkey.service.ts` stamps it
 * in the **same UPDATE** as the replay counter on every successful authentication, and the controller
 * projects `row.lastUsedAt?.toISOString() ?? null`. ⇒ the field is declared, and a consumer may
 * render it.
 *
 * ⛔ **`null` is a PERMANENT, LEGITIMATE state, not a loading artifact and not a collection failure.**
 * The column is nullable with no default and was NOT backfilled — a `DEFAULT now()` would have
 * asserted a use that never happened — so `null` means *no authentication recorded since the column
 * existed*. ⇒ a consumer renders **`kind="empty"`** for it; `notcollected` would send an operator
 * hunting for a broken pipeline that does not exist.
 *
 * ⚠️ **The history is kept rather than deleted** because the shape it warns about is easy to
 * re-create: a producer that sends a key it cannot populate is worse than one that omits it, and the
 * next author to add a field here should reach for the same test — does a column read back it?
 *
 * ⚠️ `displayName` is `string`, never `null`, because the controller already applies
 * `row.deviceName ?? ''` (`:104`). ⛔ It can therefore be the EMPTY STRING, which is not the same
 * as "named" — every consumer must handle that, and `PasskeyDialog` does.
 */
export interface OperatorPasskey {
  readonly id: string;
  readonly displayName: string;
  readonly createdAt: string;
  /** ⭐ ISO-8601, or `null` for *never used* — see the docblock. Both states are permanent and honest. */
  readonly lastUsedAt: string | null;
}

/** ⛔ Exported so a path test can assert the wire contract without mounting a component. */
export const PASSKEYS_PATH = '/v1/auth/passkeys';
export const PASSKEY_REGISTER_OPTIONS_PATH = '/v1/auth/passkey/register/options';

/**
 * ⛔ `encodeURIComponent`, even though the id is a server-minted uuid. The rule is about the
 * SHAPE of the code, not this call: an un-encoded interpolation is the same line whether the value
 * came from the server or from a text field, so reviewers cannot tell the safe one from the unsafe
 * one at a glance. Every other id path in this package encodes (`identity.api.ts:571`).
 */
export function passkeyPath(id: string): string {
  return `${PASSKEYS_PATH}/${encodeURIComponent(id)}`;
}

export function listMyPasskeys(): Promise<readonly OperatorPasskey[]> {
  return api.get<readonly OperatorPasskey[]>(PASSKEYS_PATH);
}

export function myPasskeyRegistrationOptions(): Promise<PasskeyRegistrationOptions> {
  return api.post<PasskeyRegistrationOptions>(PASSKEY_REGISTER_OPTIONS_PATH);
}

/**
 * Complete an enrolment. Body shape is `registerPasskeySchema`
 * (`dto/register-passkey.dto.ts:31-41`): `{ response, deviceName? }`, strict — an extra key is a
 * 400 from `ZionStrictValidationPipe`, so ⛔ do not add fields here speculatively.
 *
 * ⛔ Typed `Promise<void>` even though the route DOES return a body, and the `void` is deliberate:
 * there is nothing here any UI should read, and declaring it `void` makes that un-consumable rather
 * than merely unused. The caller re-reads the LIST endpoint.
 *
 * ✅ **The response no longer carries key material (#1234).** The route used to `.returning()` the
 * whole `operator_passkeys` row and pass it through unprojected — `publicKey` bytes, `credentialId`
 * and `counter` all on the wire. The controller now projects onto `OperatorPasskeySummary`
 * (`{ id, displayName, createdAt, lastUsedAt }`), so those three fields are gone. ⛔ **This paragraph
 * used to describe the leak as live** ("the response carries `publicKey` bytes …"); it is corrected
 * here rather than deleted, so a reader who remembers the old comment can see the projection landed
 * rather than suspecting it was reverted.
 */
export function registerMyPasskey(
  deviceName: string,
  response: RegistrationResponseJSON,
): Promise<void> {
  return api.post<void>(PASSKEYS_PATH, { deviceName, response });
}

/** 204 No Content on success; 404 when the id is not the caller's (`passkey.service.ts:335-337`). */
export function deleteMyPasskey(id: string): Promise<void> {
  return api.delete<void>(passkeyPath(id));
}

/**
 * The browser half of enrolment — **the one place `@simplewebauthn/browser` is called for
 * registration.**
 *
 * ⛔ `startRegistration({ optionsJSON })`, NEVER `navigator.credentials.create()` with raw server
 * options. That is a standing repo rule (CLAUDE.md, "WebAuthn client"), and the reason it is a rule
 * rather than a style note is that the server's options are base64url JSON while the platform API
 * wants `ArrayBuffer`s — hand-passing them fails at runtime in a way that reads like a broken
 * authenticator rather than a coding error.
 *
 * Isolated into its own exported function so the dialog can take it as a behavioural SEAM: happy-dom
 * implements no WebAuthn at all, and the repo bans `vi.fn()`/`vi.mock()`, so the only way to assert
 * the enrolment flow is to hand the component a real function that returns a real
 * `RegistrationResponseJSON`.
 */
export function createPasskeyCredential(
  optionsJSON: PasskeyRegistrationOptions,
): Promise<RegistrationResponseJSON> {
  return startRegistration({ optionsJSON });
}

/**
 * The route of the operator's own recovery codes (kernel apps stage 5, WS-31a1, R-152). It is the
 * `RECOVERY_CODES_ROUTE` of `@zion/sdk-contracts`, also the `useApiResource` key of the count.
 */
export const RECOVERY_CODES_PATH = RECOVERY_CODES_ROUTE;

/** What a recovery-code answer shows when its 200 body fails the contract. */
export const RECOVERY_CODES_RESPONSE_ERROR_MESSAGE = 'The recovery code answer did not match the contract';

/**
 * The count of the caller's unused recovery codes: `GET /v1/auth/recovery-codes`.
 *
 * Self-service, like the four passkey routes above: `CpJwtAuthGuard` and
 * `NoDelegatedPrincipalGuard`, and the operator id comes from the token.
 *
 * @returns `{ remaining }`, an integer from 0 to 10, parsed with `recoveryCodesStatusSchema`.
 * @throws Error(RECOVERY_CODES_RESPONSE_ERROR_MESSAGE) with the `ZodError` as its `cause` on a 200
 *   body the contract refuses; `ApiError` on a non-2xx (401 for a session that ended, 403 for a
 *   delegated token); the raw failure on a network fault.
 */
export async function getRecoveryCodesStatus(): Promise<RecoveryCodesStatus> {
  const parsed = recoveryCodesStatusSchema.safeParse(await api.get<unknown>(RECOVERY_CODES_PATH));
  if (!parsed.success) throw new Error(RECOVERY_CODES_RESPONSE_ERROR_MESSAGE, { cause: parsed.error });
  return parsed.data;
}

/**
 * Replace every recovery code of the caller: `POST /v1/auth/recovery-codes` with no body.
 *
 * Every older code stops working. The answer is the only time the ten new codes cross the wire, so
 * the caller shows them once and keeps them in React state only, until the operator closes the
 * reveal (`OneTimeSecretDialog`).
 *
 * @returns `{ recoveryCodes }`: exactly ten codes `XXXX-XXXX-XXXX`, no repeat.
 * @throws Error(RECOVERY_CODES_RESPONSE_ERROR_MESSAGE) on a 200 body the contract refuses; `ApiError`
 *   on a non-2xx: 401 for a token from before the operator's sign-out, 403 `second_factor_required`
 *   for a session that never passed the passkey, 403 for a delegated token, 429 from the throttle
 *   (5 per 60 s); the raw failure on a network fault.
 */
export async function replaceRecoveryCodes(): Promise<RecoveryCodesIssued> {
  const parsed = recoveryCodesIssuedSchema.safeParse(await api.post<unknown>(RECOVERY_CODES_PATH));
  if (!parsed.success) throw new Error(RECOVERY_CODES_RESPONSE_ERROR_MESSAGE, { cause: parsed.error });
  return parsed.data;
}
